import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import { useSearchParams } from "react-router-dom";
import {
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  filterObjectToString,
  filterStringToObject,
} from "utils/functions/reactTableFilterConverter";

const rowPerPageList = [10, 25, 50, 100, 200];

export const ReactTable = ({
  columns,
  data,
  setSelectedRowId,
  getRowStyle,
}) => {
  const [rowSelection, setRowSelection] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({});
  const [allParams, setAllParams] = useState({
    sort: "",
    order: "",
    page: 1,
    rowCount: 10,
    filters: "",
  });

  useEffect(() => {
    const paramsToObject = Object.fromEntries(searchParams.entries());
    setAllParams(paramsToObject);

    if (paramsToObject?.filters) {
      const existingFiltersTemp = {
        ...filterStringToObject(paramsToObject?.filters),
      };
      setFilters(existingFiltersTemp);
    }
  }, [searchParams]);

  const handleSortChange = (sortingHead) => {
    setSearchParams({
      ...allParams,
      sort: sortingHead,
      order: searchParams.get("order") === "ASC" ? "DESC" : "ASC",
    });
  };

  const handleChangePage = (event, newPage) => {
    setSearchParams({
      ...allParams,
      page: newPage.toString() || 1,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowCount = event.target.value;

    setSearchParams({
      ...allParams,
      rowCount: newRowCount || 10,
    });
  };

  const handleFilterValueChange = (value, field) => {
    const tempFilter = {
      ...filters,
      [field]: value,
    };
    setFilters({ ...tempFilter });
  };

  const handleFilterEnter = (e) => {
    if (e.key === "Enter") {
      setSearchParams({
        ...allParams,
        filters: filterObjectToString(filters),
      });
    }
  };
  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.lotPartId,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const selectedIds = table
      .getSelectedRowModel()
      .rows.map((row) => row.original);
    setSelectedRowId(selectedIds);
  }, [rowSelection]);

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          maxHeight: "68vh",
          overflow: "auto",
          padding: 0,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                sx={{ backgroundColor: "#f5f7fa" }}
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f7fa",
                        borderBottom: "2px solid #e0e0e0",
                        color: "#2c3e50",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <FlexBox
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {!header.column.columnDef.columns && (
                          <IconButton
                            onClick={() => handleSortChange(header.id)}
                            padding="none"
                          >
                            {allParams.sort === header.id ? (
                              allParams.order === "ASC" ? (
                                <PlayArrowIcon
                                  fontSize="small"
                                  style={{ transform: "rotate(270deg)" }}
                                />
                              ) : allParams.order === "DESC" ? (
                                <PlayArrowIcon
                                  fontSize="small"
                                  style={{ transform: "rotate(90deg)" }}
                                />
                              ) : (
                                <SwapVertIcon fontSize="small" />
                              )
                            ) : (
                              <SwapVertIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </FlexBox>
                      {header.column.getCanFilter() ? (
                        <div>
                          <TextField
                            variant="standard"
                            fullWidth
                            placeholder="Filter..."
                            onChange={(e) =>
                              handleFilterValueChange(e.target.value, header.id)
                            }
                            onKeyUp={(e) => handleFilterEnter(e)}
                            // value={
                            //   Object.keys(filters).includes(header.id)
                            //     ? filters[header.id]
                            //     : ""
                            // }
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "4px",
                              },
                            }}
                          />
                        </div>
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                style={getRowStyle ? getRowStyle(row.original) : undefined}
                sx={{
                  "&:hover": {
                    backgroundColor: "#fafafa",
                  },
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    sx={{ color: "#333", borderBottom: "1px solid #eee" }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginTop: "8px" }}
      >
        <Box style={{ fontSize: "14px" }}>
          {/* Total Rows: <strong>100</strong> */}
        </Box>
        <FlexBox justifyContent="flex-end" alignItems="center">
          <Typography>Row per page</Typography>
          <FormControl
            variant="standard"
            sx={{ m: 1, minWidth: 60, textAlign: "center" }}
            size="small"
          >
            <Select
              labelId="demo-simple-select-label"
              id="rows-per-page-select"
              value={allParams?.rowCount || 10}
              label="Rows per page"
              onChange={handleChangeRowsPerPage}
            >
              {rowPerPageList.map((value, index) => {
                return (
                  <MenuItem
                    key={index}
                    value={value}
                    selected={allParams.rowCount === value}
                  >
                    {value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Pagination
            count={100}
            color="primary"
            page={parseInt(allParams.page) || 1}
            onChange={handleChangePage}
          />
        </FlexBox>
      </FlexBox>
    </Box>
  );
};
